<template>
  <div class="overflow-auto h-full">
    <div
      class="flex flex-wrap sm:flex-nowrap items-center justify-between my-4 lg:my-8"
    >
      <!-- title -->
      <div class="task-1 flex-grow flex items-center">
        <svg
          viewBox="0 0 40 40"
          class="block w-8 h-8 stroke-current text-navy"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)">
            <path
              d="M13.045,18.636l-3.712.53.53-3.712,9.546-9.546A2.25,2.25,0,0,1,22.591,9.09Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.250 0.749 L12.750 0.749 L12.750 3.749 L5.250 3.749 Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M12.75,2.249h3a1.5,1.5,0,0,1,1.5,1.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M17.25,18.749v3a1.5,1.5,0,0,1-1.5,1.5H2.25a1.5,1.5,0,0,1-1.5-1.5v-18a1.5,1.5,0,0,1,1.5-1.5h3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25 8.249L12.75 8.249"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25 12.749L8.25 12.749"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
          </g>
        </svg>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          New Task
        </span>
      </div>

      <!-- Back to Task List -->
      <div class="task-2 sm:task-3 sm:mr-3">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.task.index')"
        >
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-7 h-7 stroke-current text-white"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M7.501 11.997L16.501 11.997"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
              <path
                d="M11.251 15.747L7.501 11.997 11.251 8.247"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
            </g>
          </svg>
          <span class="sr-only"> Task List </span>
        </inertia-link>
      </div>
    </div>

    <flash-message />
    <!-- Step 1 -->
    <div class="py-3 px-2 mt-4 bg-white">
      <div class="text-black font-bold mb-2">{{ stepTitle }}</div>
      <div v-if="step == 1">
        <!-- Farmer Selection -->
        <div class="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label class="block text-sm font-medium text-gray-700 mb-3">Select Farmer</label>
          
          <div class="relative">
            <button
              type="button"
              v-on:click="farmer.open = !farmer.open"
              v-click-out-side="closeFarmer"
              class="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              aria-haspopup="listbox"
              aria-expanded="true"
              aria-labelledby="listbox-label"
            >
              <input
                type="text"
                class="block w-full truncate p-0 border-none placeholder-gray-300 focus:ring-0"
                v-model="farmer.search"
                @keyup="searchFarmer"
                :placeholder="farmerPlaceholderText"
              />

              <!-- Dropdown icon -->
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                v-show="Object.entries(farmer.farmers).length > 0"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </button>

            <div
              class="absolute w-full z-10 mt--1 bg-white shadow-lg rounded-b-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              v-show="farmer.open && (Object.entries(farmer.farmers).length > 0 || (farmer.search && farmer.search.length >= 3 && showCreateFarmerOption))"
            >
              <!-- Scrollable results area -->
              <ul
                class="max-h-80 py-1 overflow-auto"
                tabindex="-1"
                role="listbox"
                aria-labelledby="listbox-label"
                aria-activedescendant="listbox-option-3"
                v-if="Object.entries(farmer.farmers).length > 0"
              >
                <li
                  class="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer select-none relative"
                  role="option"
                  v-for="(farmer, index) in farmer.farmers"
                  :key="farmer.id"
                  v-on:click="selectFarmer(index, farmer)"
                >
                  <div class="p-4 flex items-start space-x-3" :class="farmer.chosen == index ? 'bg-indigo-50' : ''">
                    <!-- Selection indicator -->
                    <div class="flex-shrink-0 flex items-center">
                      <div class="w-4 h-4 flex items-center justify-center">
                        <svg
                          v-if="farmer.chosen == index"
                          class="w-4 h-4 text-indigo-600"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    
                    <!-- Farmer info card -->
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-medium text-gray-900 truncate" :class="farmer.chosen == index ? 'font-semibold' : ''">
                        {{ farmer.name }}
                      </div>
                      <div class="text-xs text-gray-500 mt-1 space-y-0.5">
                        <div v-if="farmer.address" class="truncate">{{ farmer.address }}</div>
                        <div class="flex items-center space-x-3">
                          <span v-if="farmer.account_number" class="flex-shrink-0">Account: {{ farmer.account_number }}</span>
                          <span v-if="farmer.herd_no" class="flex-shrink-0">Herd: {{ farmer.herd_no }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
              
              <!-- Fixed "Add new farmer" option at bottom -->
              <div
                v-if="farmer.search && farmer.search.length >= 3 && showCreateFarmerOption"
                class="border-t border-gray-200 bg-blue-50 hover:bg-blue-100 cursor-pointer select-none"
                role="option"
                @click="createNewFarmer"
              >
                <div class="p-4 flex items-center space-x-3">
                  <div class="flex-shrink-0 flex items-center">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-blue-900">
                      Add "{{ farmer.search }}" as new farmer
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>

        <div class="w-4/12 py-2 px-4 text-xs text-gray-600" v-if="address_line_1">
          {{ [address_line_1, address_line_2, city, county].filter(Boolean).join(', ') }}
        </div>
      </div>
      <div class="p-4 text-sm" v-if="step == 2">
        <!-- Collection Method Selection -->
        <div class="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label class="block text-sm font-medium text-gray-700 mb-4">Collection Method</label>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Mobile Collection Card -->
            <div 
              class="relative cursor-pointer rounded-lg border-2 transition-all duration-200 hover:shadow-lg overflow-hidden h-32"
              :class="form.collection_method === 'mobile' 
                ? 'border-indigo-600 bg-indigo-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-gray-300'"
              @click="form.collection_method = 'mobile'"
            >
              <input
                type="radio"
                v-model="form.collection_method"
                value="mobile"
                class="sr-only"
              />
              
              <div class="flex h-full">
                <!-- Left side - Icon/Image section -->
                <div class="w-32 flex items-center justify-center"
                     :class="form.collection_method === 'mobile' 
                       ? 'bg-indigo-100' 
                       : 'bg-gray-100'"
                >
                  <svg class="w-16 h-16" :class="form.collection_method === 'mobile' ? 'text-indigo-600' : 'text-gray-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z" />
                  </svg>
                </div>

                <!-- Right side - Content section -->
                <div class="flex-1 p-4 flex flex-col justify-center relative">
                  <!-- Selection indicator -->
                  <div class="absolute top-3 right-3">
                    <div class="flex items-center justify-center w-5 h-5 rounded-full"
                         :class="form.collection_method === 'mobile' 
                           ? 'bg-indigo-600' 
                           : 'bg-gray-200'"
                    >
                      <svg v-if="form.collection_method === 'mobile'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  <h3 class="text-lg font-semibold text-gray-900 mb-1">Mobile Collection</h3>
                  <p class="text-sm text-gray-600 mb-2">Use Scout+ mobile app for field collection</p>
                  <div class="flex items-center space-x-2 text-xs text-gray-500">
                    <span class="bg-green-100 text-green-800 px-2 py-1 rounded-full">Recommended</span>
                    <span>•</span>
                    <span>Offline capable</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manual Entry Card -->
            <div 
              class="relative cursor-pointer rounded-lg border-2 transition-all duration-200 hover:shadow-lg overflow-hidden h-32"
              :class="form.collection_method === 'manual' 
                ? 'border-indigo-600 bg-indigo-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-gray-300'"
              @click="handleManualSelection"
            >
              <input
                type="radio"
                v-model="form.collection_method"
                value="manual"
                class="sr-only"
              />
              
              <div class="flex h-full">
                <!-- Left side - Icon/Image section -->
                <div class="w-32 flex items-center justify-center"
                     :class="form.collection_method === 'manual' 
                       ? 'bg-indigo-100' 
                       : 'bg-gray-100'"
                >
                  <svg class="w-16 h-16" :class="form.collection_method === 'manual' ? 'text-indigo-600' : 'text-gray-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>

                <!-- Right side - Content section -->
                <div class="flex-1 p-4 flex flex-col justify-center relative">
                  <!-- Selection indicator -->
                  <div class="absolute top-3 right-3">
                    <div class="flex items-center justify-center w-5 h-5 rounded-full"
                         :class="form.collection_method === 'manual' 
                           ? 'bg-indigo-600' 
                           : 'bg-gray-200'"
                    >
                      <svg v-if="form.collection_method === 'manual'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  <h3 class="text-lg font-semibold text-gray-900 mb-1">Manual Entry</h3>
                  <p class="text-sm text-gray-600 mb-2">Enter samples directly in web application</p>
                  <div class="flex items-center space-x-2 text-xs text-gray-500">
                    <span class="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">No sync</span>
                    <span>•</span>
                    <span>Immediate entry</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lab Selection -->
        <div class="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50" v-if="form.collection_method !== ''">
          <label class="block text-sm font-medium text-gray-700 mb-4">Select Lab</label>

          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="(lab, key) in list_available_labs"
              :key="key"
              class="relative rounded-lg border-2 transition-all duration-200 overflow-hidden h-20"
              :class="[
                isLabDisabled(lab.id) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md',
                isLabDisabled(lab.id)
                  ? 'border-gray-200 bg-gray-100'
                  : form.lab === lab.id
                    ? 'border-indigo-600 bg-indigo-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              ]"
              :title="isLabDisabled(lab.id) ? lab_account_missing_message : ''"
              @click="selectLab(lab.id)"
            >
              <input
                type="radio"
                :value="lab.id"
                v-model="form.lab"
                class="sr-only"
              />
              <div class="flex h-full items-center p-3">
                <div class="flex-shrink-0 mr-3">
                  <svg class="w-8 h-8" :class="form.lab === lab.id ? 'text-indigo-600' : 'text-gray-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1-4h1" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-medium text-gray-900 truncate">{{ lab.name }}</h3>
                  <p v-if="isLabDisabled(lab.id)" class="text-xs leading-tight text-red-600">Account not set up. Contact support@livefarm.ie</p>
                </div>
                <div class="flex-shrink-0 ml-2">
                  <div class="flex items-center justify-center w-4 h-4 rounded-full"
                       :class="form.lab === lab.id
                         ? 'bg-indigo-600'
                         : 'bg-gray-200'"
                  >
                    <svg v-if="form.lab === lab.id" class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-red-600 text-sm mt-1" v-if="form.errors?.lab">
            {{ form.errors.lab }}
          </div>
        </div>

        <div class="mt-8" v-if="form.lab !== '' && form.lab !== null">
          <!-- Show farms list only for mobile collection -->
          <div class="mb-6" v-if="form.collection_method === 'mobile'">


            <!-- Task Options (only show for mobile collection) -->
            <div class="bg-white border-2 border-gray-200 rounded-lg p-4" v-if="form.collection_method === 'mobile'">
              <div class="block text-sm font-medium text-gray-700 mb-4">
                Create Tasks - {{ Object.keys(form.tests).length }} farms available
              </div>

              <!-- Option 1: Single Task (Default) -->
              <div class="space-y-4">
                <label class="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    :value="false"
                    v-model="form.use_individual_tasks"
                    @change="handleTaskModeChange"
                    class="mt-1 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <div class="ml-3 flex-1">
                    <div class="text-sm font-medium text-gray-900">
                      Create one task for all farms (Recommended)
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      This will create <strong>one task in the Scout+ app</strong> that covers all {{ Object.keys(form.tests).length }} farms.
                    </div>
                    
                    <!-- Single task configuration -->
                    <div v-if="!form.use_individual_tasks" class="mt-3 p-3 bg-blue-50 rounded border">
                      <div class="flex gap-x-3">
                        <div class="w-2/3">
                          <div class="flex items-center gap-2 mb-1">
                            <label class="block text-xs font-medium text-gray-700">Default Test Type</label>
                            <button 
                              @click="showLabTestInfo()"
                              type="button"
                              class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                              title="View available lab tests"
                            >
                              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                              </svg>
                            </button>
                          </div>
                          <Multiselect
                            v-model="form.default_test"
                            :options="farm_tests"
                            mode="tags"
                            :max="1"
                            :disabled="Object.keys(farm_tests).length === 0"
                            placeholder="Select default lab test"
                            :close-on-select="false"
                            :searchable="true"
                            :delay="0"
                            :min-chars="0"
                            :resolve-on-load="false"
                            :filter-results="false"
                            :can-deselect="true"
                            :can-clear="true"
                          />
                        </div>
                        <div class="w-1/3">
                          <label class="block text-xs font-medium text-gray-700 mb-1">No. Samples Required</label>
                          <input
                            class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            type="number"
                            v-model="form.default_samples"
                            placeholder="Optional (informational only)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </label>

                <!-- Option 2: Individual Tasks -->
                <label class="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    :value="true"
                    v-model="form.use_individual_tasks"
                    @change="handleTaskModeChange"
                    class="mt-1 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <div class="ml-3 flex-1">
                    <div class="text-sm font-medium text-gray-900">
                      Create separate tasks for individual farms ({{ Object.keys(form.tests).length }} farms available)
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      This will create <strong>separate individual tasks in the Scout+ app</strong> for each selected farm. Choose this option if only wanting to sample a specific farm or where farms need to be sampled at different times.
                    </div>

                    <!-- Individual farm configuration -->
                    <div v-if="form.use_individual_tasks" class="mt-3 space-y-3">

                      
                      <!-- Select All/None buttons -->
                      <div class="flex justify-start gap-2 mb-3">
                        <button
                          type="button"
                          @click="selectAllFarms"
                          class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          @click="selectNoneFarms"
                          class="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                          Select None
                        </button>
                      </div>
                      
                      <div
                        v-for="(farm, farmKey) in form.tests"
                        :key="farmKey"
                        class="border rounded-lg p-3"
                        :class="farm.create_subtask ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'"
                      >
                        <div class="flex items-center mb-3">
                          <input
                            type="checkbox"
                            v-model="form.tests[farmKey].create_subtask"
                            @change="handleFarmTaskToggle(farmKey)"
                            class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <div class="ml-3">
                            <div class="text-sm font-medium text-gray-700">{{ farm.name }}</div>
                          </div>
                        </div>

                        <!-- Task configuration for this farm -->
                        <div v-if="farm.create_subtask" class="ml-6 space-y-3">
                          <div class="flex gap-x-3">
                            <div class="w-2/3">
                              <div class="flex items-center gap-2 mb-1">
                                <label class="block text-xs font-medium text-gray-600">Default Test Type</label>
                                <button 
                                  @click="showLabTestInfo()"
                                  type="button"
                                  class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                                  title="View available lab tests"
                                >
                                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                              <Multiselect
                                v-model="form.tests[farmKey].tests"
                                :options="farm_tests"
                                mode="tags"
                                @select="validateInput(farmKey)"
                                :max="1"
                                :disabled="Object.keys(farm_tests).length === 0"
                                placeholder="Select default lab test"
                                :close-on-select="false"
                                :searchable="true"
                                :delay="0"
                                :min-chars="0"
                                :resolve-on-load="false"
                                :filter-results="false"
                                :can-deselect="true"
                                :can-clear="true"
                              />
                            </div>
                            <div class="w-1/3">
                              <label class="block text-xs font-medium text-gray-600 mb-1">No. Samples Required</label>
                              <input
                                class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                type="number"
                                v-model="form.tests[farmKey].no_of_samples"
                                placeholder="Optional (informational only)"

                              />
                            </div>
                          </div>

                          <div
                            class="block text-sm text-red-700"
                            v-if="status[farmKey]?.show"
                          >
                            {{ status[farmKey]?.message }}
                          </div>
                        </div>
                      </div>


                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="step == 3">
        <div class="grid grid-cols-1 sm:grid-cols-2">
          <div class="m-2" v-if="form.collection_method === 'mobile'">
            <!-- Assign To -->
            <label for="assigned_to" class="block">Assign To</label>

            <button
              type="button"
              id="assigned_to"
              v-on:click="assigned_to.open = !assigned_to.open"
              class="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              aria-haspopup="listbox"
              aria-expanded="true"
              aria-labelledby="listbox-label"
            >
              <input
                type="text"
                class="block w-full truncate p-0 border-none placeholder-gray-300 focus:ring-0"
                v-model="assigned_to.search"
                @keyup="searchAssignedTo"
                :placeholder="assignedToPlaceholderText"
              />

              <!-- Dropdown icon -->
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                v-show="Object.entries(assigned_to.users).length > 0"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </button>

            <ul
              class="absolute w-[290px] z-10 mt--1 bg-white shadow-lg max-h-60 rounded-b-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
              tabindex="-1"
              role="listbox"
              aria-labelledby="listbox-label"
              aria-activedescendant="listbox-option-3"
              v-show="
                assigned_to.open && Object.entries(assigned_to.users).length > 0
              "
            >
              <li
                class="text-gray-900 hover:bg-gray-100 cursor-pointer select-none relative py-2 px-8"
                role="option"
                v-for="(user, index) in assigned_to.users"
                :key="user.id"
                v-on:click="selectAssignedTo(index, user)"
              >
                <span
                  class="font-normal block truncate"
                  :class="
                    assigned_to.chosen == index
                      ? 'font-semibold'
                      : 'font-normal'
                  "
                >
                  {{ user }}
                </span>

                <span
                  class="text-indigo-600 absolute inset-y-0 left-0 flex items-center pl-1.5"
                  v-if="assigned_to.chosen == index"
                >
                  <svg
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </li>
            </ul>
          </div>

          <div class="m-2" v-if="form.collection_method === 'mobile'">
            <div v-if="role == 'admin'">
              <!-- Contractor -->
              <label for="contractor">Contractor</label>

              <button
                type="button"
                id="contractor"
                v-on:click="contractor.open = !contractor.open"
                class="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                aria-haspopup="listbox"
                aria-expanded="true"
                aria-labelledby="listbox-label"
              >
                <input
                  type="text"
                  class="block w-full truncate p-0 border-none placeholder-gray-300 focus:ring-0"
                  v-model="contractor.search"
                  @keyup="searchContractor"
                  :placeholder="contractorPlaceholderText"
                />

                <!-- Dropdown icon -->
                <span
                  class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  v-show="Object.entries(contractor.contractors).length > 0"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <ul
                class="absolute w-[290px] z-10 mt--1 bg-white shadow-lg max-h-60 rounded-b-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                tabindex="-1"
                role="listbox"
                aria-labelledby="listbox-label"
                aria-activedescendant="listbox-option-3"
                v-show="
                  contractor.open &&
                  Object.entries(contractor.contractors).length > 0
                "
              >
                <li
                  class="text-gray-900 hover:bg-gray-100 cursor-pointer select-none relative py-2 px-8"
                  role="option"
                  v-for="(organisation, index) in contractor.contractors"
                  :key="organisation.id"
                  v-on:click="selectContractor(index, organisation)"
                >
                  <span
                    class="font-normal block truncate"
                    :class="
                      assigned_to.chosen == index
                        ? 'font-semibold'
                        : 'font-normal'
                    "
                  >
                    {{ organisation }}
                  </span>

                  <span
                    class="text-indigo-600 absolute inset-y-0 left-0 flex items-center pl-1.5"
                    v-if="contractor.chosen == index"
                  >
                    <svg
                      class="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="m-2" v-if="form.collection_method === 'mobile'">
            <!-- Priority -->
            <div class="">
              <label
                class="block text-sm font-medium text-gray-700"
                for="priority"
              >
                Priority
              </label>

              <select
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                id="priority"
                v-model="form.priority"
              >
                <option
                  v-for="(priority_name, key) in priorities"
                  :value="key"
                  :key="key"
                >
                  {{ priority_name }}
                </option>
              </select>
            </div>
          </div>

          <div class="m-2" v-if="form.collection_method === 'mobile'">
            <!-- Active Date -->
            <div class="">
              <label
                class="block text-sm font-medium text-gray-700"
                for="active_date"
              >
                Scheduled Date
              </label>

              <VueDatePicker
                range
                name="active_date"
                :month-change-on-scroll="false"
                v-model="form.active_date"
                :text-input="{ format: 'dd/MM/yyyy HH:mm - dd/MM/yyyy HH:mm' }"
                :format="format"
                rangeSeparator="-"
                :min-date="(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })()"
              />
            </div>
          </div>

          <div class="m-2">
            <!-- PO Lab -->
            <div class="">
              <label
                class="block text-sm font-medium text-gray-700"
                for="po_lab"
              >
                PO Lab
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                id="po_lab"
                type="text"
                v-model="form.po_lab"
              />
            </div>
          </div>

          <div class="m-2">
            <!-- PO Soil Collection -->
            <div class="">
              <label
                class="block text-sm font-medium text-gray-700"
                for="po_soil_collection"
              >
                PO Soil Collection
              </label>

              <input
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                id="po_soil_collection"
                type="text"
                v-model="form.po_soil_collection"
              />
            </div>
          </div>
        </div>

        <!-- Advanced Options (Admin Only) -->
        <div class="m-2" v-if="role === 'admin' && form.collection_method === 'mobile'">
          <div class="border border-gray-200 rounded-lg bg-gray-50">
            <button
              type="button"
              @click="showAdvancedOptions = !showAdvancedOptions"
              class="w-full p-4 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span class="text-sm font-medium text-gray-900">Advanced Options</span>
              <svg 
                :class="['w-5 h-5 text-gray-500 transition-transform duration-200', { 'rotate-180': showAdvancedOptions }]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div v-show="showAdvancedOptions" class="px-4 pb-4 border-t border-gray-200">
              <div class="mt-3">
                <label class="block text-sm font-medium text-gray-700 mb-2" for="survey_id">
                  SMAP Survey ID
                </label>
                <input
                  type="text"
                  id="survey_id"
                  v-model="form.survey_id"
                  class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., s14_1066"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Override the default SMAP survey form. Leave empty to use system default (s14_1066).
                  Format: s{number}_{number}
                </p>
                <div class="text-red-600 text-sm mt-1" v-if="form.errors?.survey_id">
                  {{ form.errors.survey_id }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="m-2" v-if="form.collection_method === 'mobile'">
          <label class="block text-sm font-medium text-gray-700" for="notes">
            Notes/Comments
          </label>

          <textarea
            class="mt-1 w-full focus:ring-indigo-500 focus:border-indigo-500 blockshadow-sm sm:text-sm border-gray-300 rounded-md"
            rows="3"
            name="notes"
            placeholder="Task notes"
            v-model="form.note"
          >
          </textarea>
        </div>
      </div>
      <div class="text-right flex justify-end">
        <div class="m-2">
          <button
            type="button"
            class="py-2 px-4 border rounded-md text-white bg-neutral-500 hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400"
            v-if="step > 1"
            v-on:click="goBack()"
          >
            Back
          </button>
        </div>
        <div class="m-2">
          <div v-if="message" class="text-red text-left text-[12px]">
            {{ message }}
          </div>
          <button
            type="button"
            class="py-2 px-4 border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
            :disabled="nextButtonDisabled"
            v-on:click="nextStep()"
          >
            {{ step == 3 ? "Create" : "Next" }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Lab Test Info Modal -->
  <div 
    v-if="showLabTestModal" 
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="closeLabTestModal"
  >
    <div 
      class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto"
      @click="handleModalClick"
    >
      <div class="p-6">
        <!-- Modal Header -->
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Available Lab Tests</h3>
          <button 
            @click="closeLabTestModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Lab Selection Dropdown -->
        <div class="mb-4" v-if="availableLabs.length > 1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select Laboratory</label>
          <select 
            v-model="selectedLabId" 
            @change="changeSelectedLab($event.target.value)"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option v-for="lab in availableLabs" :key="lab.value" :value="lab.value">
              {{ lab.label }}
            </option>
          </select>
        </div>



        <!-- Tests Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test Name</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="test in modalLabTests" :key="test.id || test.code" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ test.name || test.label }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">
                  {{ test.description || 'No description available' }}
                </td>
              </tr>
            </tbody>
          </table>
          
          <!-- No tests message -->
          <div v-if="modalLabTests.length === 0" class="text-center py-8 text-gray-500">
            No tests available for this laboratory.
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="mt-6 flex justify-end">
          <button 
            @click="closeLabTestModal"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import AppLayout from "@/Layouts/App";
import { useForm } from "@inertiajs/inertia-vue3";
import { useToast } from "vue-toastification";
import { getCurrentInstance } from "vue";
import _ from "lodash";
// import Datepicker from "vue3-datepicker";
import clickOutSide from "@mahdikhashan/vue3-click-outside";
import Multiselect from "@vueform/multiselect";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  components: {
    VueDatePicker,
    Multiselect,
  },
  layout: [AppLayout],
  props: {
    role: String,
    priorities: Object,
    create_farmer: Object,
  },
  directives: {
    clickOutSide,
  },

  setup() {
    const instance = getCurrentInstance();
    const form = useForm({
      farmer_hashid: "",
      assigned_to_hashid: "",
      contractor_hashid: "",
      lab: "",
      default_test: [], // For the single task mode
      default_samples: "", // For the single task mode
      use_individual_tasks: false, // Toggle between modes
      tests: [], // Individual farm tasks (only used when use_individual_tasks is true)
      priority: "",
      active_date: [],
      po_soil_collection: "",
      po_lab: "",
      note: "",
      collection_method: "mobile", // Default to mobile collection
      survey_id: "", // Custom SMAP survey ID for admin override
    });

    const toast = useToast();

    const confirmManualEntry = async (event) => {
      // Only show confirmation when switching to manual
      if (event && event.target && event.target.value === 'manual') {
        const result = await instance.proxy.$swal.fire({
          title: 'Manual Collection Method',
          html: 'This task will <strong>not sync</strong> with the mobile app and samples must be entered directly in the web application.<br><br>Continue with manual collection?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, use manual entry',
          cancelButtonText: 'No, keep mobile sync'
        });

        if (!result.isConfirmed) {
          form.collection_method = 'mobile';
        } else {
          form.use_individual_tasks = false;
        }
      }
    }

    return { form, toast, confirmManualEntry };
  },

  data() {
    return {
      step: 1,
      farmer: {
        search: "",
        chosen: "",
        open: false,
        farmers: {},
      },
      assigned_to: {
        search: "",
        chosen: "",
        open: false,
        users: {},
      },
      contractor: {
        search: "",
        chosen: "",
        open: false,
        contractors: {},
      },
      address_line_1: "",
      address_line_2: "",
      city: "",
      county: "",
      list_available_labs: [],
      missing_lab_accounts: [],
      lab_account_missing_message: "",
      list_available_test: [],
      farm_tests: [],
      status: [],
      allowToCreateTask: 0,
      farms_location_set: false,
      message: "",
      
      // Lab test info modal properties
      showLabTestModal: false,
      selectedLabId: null,
      
      // Advanced Options properties
      showAdvancedOptions: false,

    };
  },

  computed: {
    stepTwoClass() {
      let style = this.step === 2 ? "border-black" : "border-gray-400";
      if (this.step === 3) {
        style += " bg-gray-400 text-white";
      }

      return style;
    },

    farmerPlaceholderText() {
      if (Object.keys(this.farmer.farmers).length > 0) {
        return "Select Farmer...";
      }

      return "Search Farmer...";
    },

    assignedToPlaceholderText() {
      if (Object.keys(this.assigned_to.users).length > 0) {
        return "Select Sampler...";
      }

      return "Search Sampler...";
    },

    contractorPlaceholderText() {
      if (Object.keys(this.contractor.contractors).length > 0) {
        return "Select Contractor...";
      }

      return "Search Contractor...";
    },
    nextButtonDisabled() {
      if (this.step === 1 && !this.farms_location_set) return true;
      if (this.step === 2 && this.form.lab === "") return true;
      if (this.step === 3 && this.form.collection_method === 'mobile' && this.assigned_to.chosen === "") return true;
      return false;
    },
    stepTitle() {
      if (this.step === 1) return "Select Farmer";
      if (this.step === 2) return "Configure Lab Tests & Farms";
      if (this.step === 3) return "Task Details";
    },

    selectedFarmsCount() {
      return this.form.tests.filter(farm => farm.create_subtask).length;
    },

    showCreateFarmerOption() {
      return this.farmer.search && this.farmer.search.length >= 3 && 
             ['admin', 'consultant', 'consultant_user'].includes(this.role);
    },

    // Lab test modal computed properties
    availableLabs() {
      return this.list_available_labs.map(lab => ({
        value: lab.id,
        label: lab.name
      }));
    },

    transformedLabTests() {
      if (!this.list_available_test || this.list_available_test.length === 0) {
        return {};
      }
      
      const grouped = {};
      this.list_available_test.forEach(test => {
        if (!grouped[test.lab_id]) {
          grouped[test.lab_id] = [];
        }
        grouped[test.lab_id].push(test);
      });
      
      return grouped;
    },

    modalLabTests() {
      // Use the lab from form.lab first, then fall back to selectedLabId
      const labId = this.form.lab || this.selectedLabId;
      if (!labId || !this.transformedLabTests[labId]) {
        return [];
      }
      return this.transformedLabTests[labId];
    },

    selectedLabName() {
      // Use the lab from form.lab first, then fall back to selectedLabId
      const labId = this.form.lab || this.selectedLabId;
      const lab = this.list_available_labs.find(lab => lab.id == labId);
      return lab ? lab.name : 'Unknown Lab';
    },
  },

  created() {
    this.form.priority = "normal";

    if (this.create_farmer != null) {
      this.selectFarmer(this.create_farmer.hashid, this.create_farmer);
    }

    this.getFarmers();

    const startDate = new Date();
    const endDate = null;
    this.form.active_date = [startDate, endDate];
  },

  watch: {
    "form.lab"(newValue) {
      this.farm_tests = this.list_available_test
        .filter((test) => test.lab_id == this.form.lab)
        .reduce((result, test) => {
          result[test.code] = test.name;
          return result;
        }, {});
      
      // Sync selectedLabId with form.lab for modal
      this.selectedLabId = newValue;
    },
    "form.collection_method"(newValue, oldValue) {
      // Always reset use_individual_tasks to false when switching away from manual
      if (oldValue === 'manual' && newValue !== 'manual') {
        this.form.use_individual_tasks = false;
      }
    },
  },

  methods: {
    selectLab(labId) {
      if (this.isLabDisabled(labId)) return;
      this.form.lab = labId;
      this.selectedLabId = labId;
      this.toggleLab();
    },

    /**
     * Labs that cannot be chosen until the organisation's account for them is set
     */
    isLabDisabled(labId) {
      return this.missing_lab_accounts.includes(Number(labId));
    },

    validateInput(farmKey) {
      this.status[farmKey] = {
        show: false,
        message: "",
      };
      //only allow one test per farm
      if (this.form.tests[farmKey].tests.length > 1) {
        this.status[farmKey] = {
          show: true,
          message: "Note, only one test may be selected.",
        };
        //remove the test added
        this.form.tests[farmKey].tests.pop();
      }

      //check if either SR2 or SR3, same as SR2 + organic Matter or SR3 + organic Matter for FBA lab_tests
      // 1 represent FBA lab_tests
      if (this.form.lab == 1) {
        if (
          ["101", "103"].every((i) =>
            this.form.tests[farmKey].tests.includes(i)
          ) ||
          ["101", "104"].every((i) =>
            this.form.tests[farmKey].tests.includes(i)
          ) ||
          ["102", "104"].every((i) =>
            this.form.tests[farmKey].tests.includes(i)
          ) ||
          ["102", "103"].every((i) =>
            this.form.tests[farmKey].tests.includes(i)
          )
        ) {
          this.form.tests[farmKey].tests.pop();
          this.status[farmKey] = {
            show: true,
            message: "Note both SR2 and SR3 test can't be selected as test",
          };
        }
      }
    },

    async handleManualSelection() {
      // Show confirmation when switching to manual
      const result = await this.$swal.fire({
        title: 'Manual Collection Method',
        html: 'This task will <strong>not sync</strong> with the mobile app and samples must be entered directly in the web application.<br><br>Continue with manual collection?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, use manual entry',
        cancelButtonText: 'No, keep mobile sync'
      });

      if (result.isConfirmed) {
        this.form.collection_method = 'manual';
        this.form.use_individual_tasks = false;
      }
      // If cancelled, do nothing (keep current selection)
    },

    handleTaskModeChange() {
      if (this.form.use_individual_tasks) {
        // Switching to individual mode - clear default settings
        this.form.default_test = [];
        this.form.default_samples = "";
        
        // Initialize all farms as selected for creating subtasks
        Object.keys(this.form.tests).forEach((key) => {
          this.form.tests[key].create_subtask = true;
          this.form.tests[key].tests = [];
          this.form.tests[key].no_of_samples = "";
        });
        // Set default test for all selected farms
        Object.keys(this.form.tests).forEach((key) => {
          this.setDefaultTestForFarm(key);
        });
      } else {
        // Switching back to default mode - set default test
        this.setDefaultTest();
        
        // Clear individual farm settings
        Object.keys(this.form.tests).forEach((key) => {
          this.form.tests[key].create_subtask = false;
          this.form.tests[key].tests = [];
        });
      }
    },

    handleFarmTaskToggle(farmKey) {
      if (this.form.tests[farmKey].create_subtask) {
        // Farm was selected - set default test
        this.setDefaultTestForFarm(farmKey);
      } else {
        // Farm was deselected - clear its settings
        this.form.tests[farmKey].tests = [];
        this.form.tests[farmKey].no_of_samples = "";
      }
    },

    selectAllFarms() {
      Object.keys(this.form.tests).forEach((farmKey) => {
        this.form.tests[farmKey].create_subtask = true;
        this.setDefaultTestForFarm(farmKey);
      });
    },

    selectNoneFarms() {
      Object.keys(this.form.tests).forEach((farmKey) => {
        this.form.tests[farmKey].create_subtask = false;
        this.form.tests[farmKey].tests = [];
        this.form.tests[farmKey].no_of_samples = "";
      });
    },

    setDefaultTest() {
      const defaultTest = this.getDefaultTest();
      if (defaultTest) {
        this.form.default_test = [defaultTest.code];
      }
    },

    setDefaultTestForFarm(farmKey) {
      const defaultTest = this.getDefaultTest();
      if (defaultTest) {
        this.form.tests[farmKey].tests = [defaultTest.code];
      }
    },

    getDefaultTest() {
      if (this.list_available_test[0]?.pivot) {
        return this.list_available_test.find((test) => {
          return test.pivot?.default == 1 && this.form.lab == test.lab_id;
        });
      } else {
        return this.list_available_test.find((test) => {
          return test.is_default == 1 && this.form.lab == test.lab_id;
        });
      }
    },
    closeFarmer() {
      this.farmer.open = false;
    },

    proceedToNextStage() {
      if (!this.form.use_individual_tasks) {
        // Default mode - check if default test is selected
        if (this.form.default_test.length > 0) {
          this.step = 3;
        } else {
          this.toast.error("Please select a test for the task!");
        }
      } else {
        // Individual mode - check if at least one farm has subtask enabled
        const hasSelectedFarms = this.form.tests.some(farm => 
          farm.create_subtask && farm.tests.length > 0
        );
        
        if (hasSelectedFarms) {
          this.step = 3;
        } else {
          this.toast.error("Please select at least one farm to create a subtask for!");
        }
      }
    },

    format(dates) {
      let formatted_date = [];
      dates.forEach((date) => {
        let formate = "";
        if (date) {
          const day = date.getDate();
          const month = date.getMonth() + 1;
          const year = date.getFullYear();
          const hours = date.getHours();
          const minutes = date.getMinutes();
          const seconds = date.getSeconds();

          formate = `${day}/${month}/${year} ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }

        formatted_date.push(formate);
      });

      return `${formatted_date[0]} - ${formatted_date[1]} `;
    },

    searchFarmer: _.debounce(function () {
      if (!this.farmer.search || this.farmer.search.length < 3) {
        // If search is cleared or too short, restore original farmer list
        if (!this.farmer.search || this.farmer.search.length === 0) {
          this.getFarmers();
          this.farmer.open = true;
        }
        return;
      }

      this.getFarmers();
      this.farmer.open = true;
    }, 750),

    getFarmers() {
      let search = {
        search: this.farmer.search,
      };
      let vm = this;

      axios.post(route("app.search-farmer", search)).then(function (response) {
        let farmers = response.data.farmers;

        if (Object.keys(farmers).length == 1) {
          for (const [index, farmer] of Object.entries(farmers)) {
            vm.selectFarmer(index, farmer);
            break;
          }
          vm.farmer.farmers = "";
        } else if (Object.keys(farmers).length > 1) {
          vm.farmer.farmers = farmers;
        } else {
          vm.farmer.farmers = "";
        }
      });
    },

    selectFarmer(index, farmer) {
      this.farmer.chosen = index;
      this.farmer.search = farmer.name;
      this.farmer.open = false;
      this.message = "";

      this.getAssignedTo();

      let search = {
        hashid: index,
      };
      let vm = this;

      axios
        .post(route("app.farmer-info", search))
        .then(function (response) {
          let farmer = response.data;

          vm.address_line_1 = farmer.address_line_1;
          vm.address_line_2 = farmer.address_line_2;
          vm.city = farmer.city;
          vm.county = farmer.county;
          vm.form.tests = farmer.farm_tests;
          
          // Initialize create_subtask property for each farm
          Object.keys(vm.form.tests).forEach((key) => {
            vm.form.tests[key].create_subtask = false;
            vm.form.tests[key].no_of_samples = "";
          });
          
          vm.list_available_test = farmer.lab_tests;
          vm.list_available_labs = farmer.labs;
          vm.missing_lab_accounts = farmer.missing_lab_accounts || [];
          vm.lab_account_missing_message = farmer.lab_account_missing_message || "";
          
          // A lab chosen for the previous farmer may be blocked for this one's organisation
          if (vm.form.lab !== "" && vm.isLabDisabled(vm.form.lab)) {
            vm.form.lab = "";
            vm.selectedLabId = null;
          }

          // Auto-set the default lab from organization settings, unless that lab is blocked.
          // Convert string to number to match lab.id type.
          const defaultLabId = Number(farmer.default_lab);
          const useDefaultLab = Boolean(farmer.default_lab) && !vm.isLabDisabled(defaultLabId);
          if (useDefaultLab) {
            vm.$nextTick(() => {
              vm.form.lab = defaultLabId;
              vm.selectedLabId = defaultLabId;
              vm.toggleLab();
            });
          }
          vm.farms_location_set = farmer.farms_location_set;

          // Enable farm location pins for ALL farmers
          if (farmer.farm_location_and_parcels) {
            vm.$root.farmer = farmer.farm_location_and_parcels;
            vm.$root.farmer.count = farmer.farms_count;
          }

          if (farmer.organisation == "ISEC") {
            //load LPIS Map Details
            vm.$root.Lpis.source = "isec";
            vm.$root.Lpis.herdNo = farmer.herd_no;
            vm.$root.Lpis.show = true;
            vm.$root.Lpis.show_zone = true;
            vm.$root.Lpis.showLpisAreaDetails = true;
            vm.$root.Lpis.filterCompletedHerdNo = true;
            
            // Only show sampler and dropoff locations for ISEC
            vm.$root.services.dropoff_points = true;
            vm.$root.services.sampler_location = true;
          } else {
            // Linked parcels come with farm_location_and_parcels
            showDafmLpis(vm.$root.Lpis);
            vm.$root.Lpis.show_zone = false;
            vm.$root.Lpis.showLpisAreaDetails = false;
            vm.$root.services.dropoff_points = false;
            vm.$root.services.sampler_location = false;
          }

          if (!farmer.farms_location_set) {
            vm.message = "Please set farm location to create a task";
          }
          
          // Only call toggleLab if no default lab was set
          if (!useDefaultLab) {
            vm.toggleLab();
          }
        })
        .catch(function (error) {
          vm.message = error.response.data.message;
        });
    },

    toggleLab() {
      if (this.form.lab !== "") {
        if (!this.form.use_individual_tasks) {
          this.setDefaultTest();
        }
      } else {
        this.form.default_test = [];
      }
      
      // Clear all individual farm settings when lab changes
      Object.keys(this.form.tests).forEach((key) => {
        this.form.tests[key].create_subtask = false;
        this.form.tests[key].tests = [];
        this.form.tests[key].no_of_samples = "";
      });
    },

    searchAssignedTo: _.debounce(function () {
      if (this.assigned_to.search.length == 0) {
        this.assigned_to = {
          search: "",
          chosen: "",
          open: "",
          users: {},
        };

        this.contractor = {
          search: "",
          chosen: "",
          open: "",
          contractors: {},
        };
      }

      this.getAssignedTo();
    }, 750),

    getAssignedTo() {
      let search = {
        search: this.assigned_to.search,
        farmer: this.farmer.chosen,
      };
      let vm = this;

      axios
        .post(route("app.search-assigned-to", search))
        .then(function (response) {
          let users = response.data.users;

          if (Object.keys(users).length == 1) {
            for (const [index, user] of Object.entries(users)) {
              vm.assigned_to.search = user;
              vm.selectAssignedTo(index, user);
              break;
            }
            vm.assigned_to.users = "";
          } else if (Object.keys(users).length > 1) {
            vm.assigned_to.users = users;
          } else {
            vm.assigned_to.users = "";
          }

          vm.assigned_to.open = false;
        });
    },

    selectAssignedTo(index, user) {
      this.assigned_to.chosen = index;
      this.assigned_to.search = user;
      this.assigned_to.open = false;

      if (this.role == "admin") {
        this.getContractor();
      }
    },

    searchContractor: _.debounce(function () {
      if (this.contractor.search.length == 0) {
        this.contractor = {
          search: "",
          chosen: "",
          open: "",
          contractors: {},
        };
      }

      this.getContractor();
    }, 750),

    getContractor() {
      let search = {
        contractor: this.assigned_to.chosen,
        search: this.contractor.search,
      };

      let vm = this;

      axios
        .post(route("app.search-contractor", search))
        .then(function (response) {
          let contractors = response.data.contractors;

          // Select always the first one (if there's any)
          if (Object.keys(contractors).length > 0) {
            for (const [index, contractor] of Object.entries(contractors)) {
              vm.contractor.search = contractor;
              vm.selectContractor(index, contractor);
              break;
            }

            vm.contractor.contractors = contractors;
          } else {
            vm.contractor.search = "";
            vm.contractor.chosen = "";
            vm.contractor.contractors = "";
          }

          vm.contractor.open = false;
        });
    },

    selectContractor(index, contractor) {
      this.contractor.chosen = index;
      this.contractor.search = contractor;
      this.contractor.open = false;
    },
    nextStep() {
      if (this.step == 1) {
        this.step = 2;
        return;
      }
      if (this.step == 2) {
        this.proceedToNextStage();
        return;
      }
      if (this.step == 3) {
        this.store();
        return;
      }
    },
    goBack() {
      this.step -= 1;
    },
    store() {
      this.form.farmer_hashid = this.farmer.chosen;
      
      // Handle assignment and scheduling based on collection method
      if (this.form.collection_method === 'mobile') {
        // Mobile collection: use selected user/contractor and user-chosen priority/dates
        this.form.assigned_to_hashid = this.assigned_to.chosen;
        this.form.contractor_hashid = this.contractor.chosen;
      } else {
        // Manual collection: auto-assign to current user with immediate execution
        this.form.assigned_to_hashid = this.$page.props.auth.user.hashid;
        this.form.contractor_hashid = "";
        this.form.priority = "normal"; // Set reasonable default for immediate manual entry
        this.form.active_date = []; // No scheduling for manual tasks - backend validation should handle this
      }

      this.form.post(route("app.task.store"), {
        onSuccess: () => {
          this.form.reset();
        },
        onError: () => {
          this.$page.props.flash.status = "error";
          this.$page.props.flash.message = this.form.errors;
        },
      });
    },

    createNewFarmer() {
      // Redirect to farmer creation page with the search term pre-populated
      const searchTerm = this.farmer.search || '';
      const createUrl = route('app.farmer.create') + '?name=' + encodeURIComponent(searchTerm);
      window.location.href = createUrl;
    },

    // Lab test info modal methods
    showLabTestInfo() {
      // Sync selectedLabId with form.lab, or default to first available lab
      if (this.form.lab) {
        this.selectedLabId = this.form.lab;
      } else if (!this.selectedLabId && this.list_available_labs.length > 0) {
        this.selectedLabId = this.list_available_labs[0].id;
      }
      this.showLabTestModal = true;
    },

    closeLabTestModal() {
      this.showLabTestModal = false;
    },

    handleModalClick(event) {
      // Don't close modal when clicking inside it
      event.stopPropagation();
    },

    changeSelectedLab(labId) {
      this.selectedLabId = labId;
    },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style scoped>
/* Fix Multiselect dropdown z-index and focus issues */
:deep(.multiselect) {
  position: relative;
}

:deep(.multiselect-dropdown) {
  z-index: 9999 !important;
  position: absolute !important;
}

:deep(.multiselect-option) {
  cursor: pointer;
  user-select: none;
}

:deep(.multiselect-option:hover) {
  background-color: #f3f4f6;
}

/* Remove unwanted focus outlines and borders */
:deep(.multiselect:focus),
:deep(.multiselect:focus-within),
:deep(.multiselect-wrapper:focus),
:deep(.multiselect-wrapper:focus-within) {
  outline: none !important;
  box-shadow: none !important;
  border-color: #d1d5db !important;
}

:deep(.multiselect-input:focus) {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

/* Clean up any blue focus rings */
:deep(.multiselect *:focus) {
  outline: none !important;
  box-shadow: none !important;
}

/* Prevent dropdown from closing on click within */
:deep(.multiselect-dropdown) {
  pointer-events: auto !important;
}

:deep(.multiselect-option) {
  pointer-events: auto !important;
}

/* Improve mobile touch interaction */
@media (max-width: 768px) {
  :deep(.multiselect-option) {
    min-height: 44px;
    display: flex;
    align-items: center;
    padding: 12px 16px;
  }
}
</style>
